import React, { useEffect, useState } from "react";
import { backend_url } from "../App";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./CSS/CRM.css";

const CAMPAIGN_TYPES = ["Birthday Wish", "Anniversary Wish", "Festival Greeting", "New Collection Alert", "Custom"];
const SEGMENTS = ["All", "VIP", "Boutique", "Reseller", "Retail", "UpcomingBirthdays", "UpcomingAnniversaries"];

const DEFAULT_TEMPLATES = {
  "Birthday Wish": "Happy Birthday {name}! 🎂 Wishing you a wonderful year ahead from all of us at Aavryaa FabHub. Here's to celebrating in style!",
  "Anniversary Wish": "Happy Anniversary {name}! 💍 Wishing you many more beautiful years together. With love, Aavryaa FabHub.",
  "Festival Greeting": "Dear {name}, wishing you and your family a joyous festive season from Aavryaa FabHub! ✨",
  "New Collection Alert": "Hi {name}, our new collection just dropped! Come take a look — we think you'll love it. — Aavryaa FabHub",
  "Custom": "Hi {name}, ",
};
const DEFAULT_SEGMENT = {
  "Birthday Wish": "UpcomingBirthdays",
  "Anniversary Wish": "UpcomingAnniversaries",
  "Festival Greeting": "All",
  "New Collection Alert": "All",
  "Custom": "All",
};

const CRM = () => {
  const [events, setEvents] = useState([]);
  const [vip, setVip] = useState([]);
  const [repeat, setRepeat] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [vipProgram, setVipProgram] = useState({ perksText: "" });
  const [perksDraft, setPerksDraft] = useState("");
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [audience, setAudience] = useState([]);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newForm, setNewForm] = useState({ name: "", type: "Custom", messageTemplate: DEFAULT_TEMPLATES.Custom, targetSegment: "All" });
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadAll(); }, []);

  const loadAll = () => {
    Promise.all([
      fetch(`${backend_url}/api/crm/upcoming-events?days=30`).then((r) => r.json()),
      fetch(`${backend_url}/api/crm/vip-customers`).then((r) => r.json()),
      fetch(`${backend_url}/api/crm/repeat-customers`).then((r) => r.json()),
      fetch(`${backend_url}/api/crm/referrals`).then((r) => r.json()),
      fetch(`${backend_url}/api/crm/vip-program`).then((r) => r.json()),
      fetch(`${backend_url}/api/campaigns`).then((r) => r.json()),
    ]).then(([e, v, r, ref, vp, camp]) => {
      if (e.success) setEvents(e.events);
      if (v.success) setVip(v.customers);
      if (r.success) setRepeat(r.customers);
      if (ref.success) setReferrals(ref.summary);
      if (vp.success) { setVipProgram(vp.program); setPerksDraft(vp.program.perksText || ""); }
      if (camp.success) setCampaigns(camp.campaigns);
    }).finally(() => setLoading(false));
  };

  const saveVipPerks = async () => {
    try {
      const res = await fetch(`${backend_url}/api/crm/vip-program`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ perksText: perksDraft }),
      });
      const data = await res.json();
      if (!data.success) throw new Error();
      setVipProgram(data.program);
      toast.success("VIP program perks saved");
    } catch {
      toast.error("Failed to save perks");
    }
  };

  const handleTypeChange = (type) => {
    setNewForm((p) => ({ ...p, type, messageTemplate: DEFAULT_TEMPLATES[type], targetSegment: DEFAULT_SEGMENT[type] }));
  };

  const createCampaign = async () => {
    if (!newForm.name.trim() || !newForm.messageTemplate.trim()) {
      toast.error("Name and message are required");
      return;
    }
    try {
      const res = await fetch(`${backend_url}/api/campaigns`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newForm),
      });
      const data = await res.json();
      if (!data.success) throw new Error();
      toast.success("Campaign created");
      setCampaigns((p) => [data.campaign, ...p]);
      setShowNewForm(false);
      setNewForm({ name: "", type: "Custom", messageTemplate: DEFAULT_TEMPLATES.Custom, targetSegment: "All" });
    } catch {
      toast.error("Failed to create campaign");
    }
  };

  const openCampaign = async (campaign) => {
    setSelectedCampaign(campaign);
    setAudience([]);
    try {
      const res = await fetch(`${backend_url}/api/campaigns/${campaign._id}/audience`);
      const data = await res.json();
      if (data.success) setAudience(data.audience);
    } catch {
      toast.error("Failed to load audience");
    }
  };

  const markSent = async (customerId) => {
    try {
      const res = await fetch(`${backend_url}/api/campaigns/${selectedCampaign._id}/mark-sent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customerId }),
      });
      const data = await res.json();
      if (!data.success) throw new Error();
      setAudience((prev) => prev.map((a) => (a.customerId === customerId ? { ...a, alreadySent: true } : a)));
      setCampaigns((prev) => prev.map((c) => (c._id === selectedCampaign._id ? data.campaign : c)));
    } catch {
      toast.error("Failed to mark sent");
    }
  };

  const deleteCampaign = async (id) => {
    if (!window.confirm("Delete this campaign?")) return;
    try {
      await fetch(`${backend_url}/api/campaigns/${id}`, { method: "DELETE" });
      setCampaigns((prev) => prev.filter((c) => c._id !== id));
      if (selectedCampaign?._id === id) { setSelectedCampaign(null); setAudience([]); }
    } catch {
      toast.error("Failed to delete campaign");
    }
  };

  return (
    <div className="crm-page">
      <div className="crm-banner">
        <h2>💜 Customer Relationship Management</h2>
        <p>Birthday/anniversary wishes, festival greetings, new-collection alerts and WhatsApp campaigns — build a message, pick an audience, and send with one click per customer.</p>
      </div>

      <div className="crm-notice">
        <strong>How sending works:</strong> each "Send" button opens WhatsApp (web or app) with the message pre-filled to that customer's number via a wa.me link — you tap Send yourself. There's no connected WhatsApp Business API here, so nothing sends automatically or in bulk without you clicking through each one.
      </div>

      {/* ── Campaigns ── */}
      <div className="crm-card crm-card-wide">
        <div className="crm-card-header">
          <h3>📣 Campaigns — Birthday / Anniversary / Festival / New Collection / Custom</h3>
          <button className="crm-new-btn" onClick={() => setShowNewForm((s) => !s)}>{showNewForm ? "Cancel" : "+ New Campaign"}</button>
        </div>

        {showNewForm && (
          <div className="crm-new-form">
            <div className="crm-new-form-row">
              <label>Name<input type="text" value={newForm.name} onChange={(e) => setNewForm((p) => ({ ...p, name: e.target.value }))} placeholder="e.g. Diwali 2026" /></label>
              <label>Type
                <select value={newForm.type} onChange={(e) => handleTypeChange(e.target.value)}>
                  {CAMPAIGN_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
              <label>Audience
                <select value={newForm.targetSegment} onChange={(e) => setNewForm((p) => ({ ...p, targetSegment: e.target.value }))}>
                  {SEGMENTS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
            </div>
            <label className="crm-new-form-message">Message <em>(use {"{name}"} to personalise)</em>
              <textarea rows={3} value={newForm.messageTemplate} onChange={(e) => setNewForm((p) => ({ ...p, messageTemplate: e.target.value }))} />
            </label>
            <button className="crm-save-btn" onClick={createCampaign}>Create Campaign</button>
          </div>
        )}

        {campaigns.length === 0 ? (
          <p className="crm-empty">No campaigns yet — create one above.</p>
        ) : (
          <div className="crm-campaign-list">
            {campaigns.map((c) => (
              <div key={c._id} className={`crm-campaign-row ${selectedCampaign?._id === c._id ? "active" : ""}`} onClick={() => openCampaign(c)}>
                <div>
                  <span className="crm-campaign-name">{c.name}</span>
                  <span className="crm-campaign-type">{c.type} · {c.targetSegment}</span>
                </div>
                <div className="crm-campaign-right">
                  <span className="crm-campaign-sent">{c.sentLog.length} sent</span>
                  <button className="crm-del-btn" onClick={(e) => { e.stopPropagation(); deleteCampaign(c._id); }}>✕</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {selectedCampaign && (
          <div className="crm-audience">
            <h4>Audience for "{selectedCampaign.name}" ({audience.length})</h4>
            {audience.length === 0 ? (
              <p className="crm-empty">No customers match this segment (or none have a phone number on file).</p>
            ) : (
              <table className="crm-audience-table">
                <thead><tr><th>Name</th><th>Message</th><th></th></tr></thead>
                <tbody>
                  {audience.map((a) => (
                    <tr key={a.customerId}>
                      <td>{a.name || "—"}</td>
                      <td className="crm-audience-msg">{a.message}</td>
                      <td className="crm-audience-actions">
                        {a.waLink ? (
                          <a href={a.waLink} target="_blank" rel="noopener noreferrer" className="crm-send-btn">Send via WhatsApp</a>
                        ) : (
                          <span className="crm-no-phone">No phone</span>
                        )}
                        <button className={`crm-sent-btn ${a.alreadySent ? "done" : ""}`} onClick={() => markSent(a.customerId)} disabled={a.alreadySent}>
                          {a.alreadySent ? "✓ Sent" : "Mark Sent"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      <div className="crm-grid">
        <div className="crm-card">
          <h3>🎂 Upcoming Birthdays &amp; Anniversaries (30 days)</h3>
          {loading ? <p className="crm-empty">Loading…</p> : events.length === 0 ? <p className="crm-empty">Nothing coming up.</p> : (
            <ul className="crm-list">
              {events.map((e, i) => (
                <li key={i}>
                  <span className={`crm-event-badge ${e.type === "Birthday" ? "crm-badge-birthday" : "crm-badge-anniversary"}`}>{e.type === "Birthday" ? "🎂" : "💍"}</span>
                  <div className="crm-list-info">
                    <span className="crm-list-name">{e.name || "—"}</span>
                    <span className="crm-list-meta">{e.phoneNumber || "no phone"} · in {e.daysAway === 0 ? "today" : `${e.daysAway}d`}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="crm-card">
          <h3>👑 VIP Customer Program</h3>
          <label className="crm-perks-label">Program perks / benefits</label>
          <textarea className="crm-perks-input" rows={3} value={perksDraft} onChange={(e) => setPerksDraft(e.target.value)} placeholder="e.g. Free alterations, priority delivery, early access to new collections…" />
          <button className="crm-save-btn crm-perks-save" onClick={saveVipPerks}>Save Perks</button>
          {loading ? <p className="crm-empty">Loading…</p> : vip.length === 0 ? <p className="crm-empty">No VIP customers yet.</p> : (
            <ul className="crm-list crm-list-scroll">
              {vip.map((c) => (
                <li key={c._id}>
                  <span className="crm-event-badge crm-badge-vip">👑</span>
                  <div className="crm-list-info">
                    <span className="crm-list-name">{c.name || "—"}</span>
                    <span className="crm-list-meta">{c.customerId} · {c.phoneNumber || "no phone"}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="crm-card">
          <h3>🔁 Repeat Customers</h3>
          {loading ? <p className="crm-empty">Loading…</p> : repeat.length === 0 ? <p className="crm-empty">No repeat customers yet (needs 2+ tailoring orders).</p> : (
            <ul className="crm-list">
              {repeat.map((c) => (
                <li key={c._id}>
                  <span className="crm-event-badge crm-badge-repeat">🔁</span>
                  <div className="crm-list-info">
                    <span className="crm-list-name">{c.name || "—"}</span>
                    <span className="crm-list-meta">{c.orderCount} orders</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="crm-card">
          <h3>🤝 Referral Tracking</h3>
          {loading ? <p className="crm-empty">Loading…</p> : referrals.length === 0 ? <p className="crm-empty">No referral sources logged yet — add one in a customer's Master Info tab.</p> : (
            <ul className="crm-list">
              {referrals.map((r, i) => (
                <li key={i}>
                  <span className="crm-event-badge crm-badge-referral">🤝</span>
                  <div className="crm-list-info">
                    <span className="crm-list-name">{r.source}</span>
                    <span className="crm-list-meta">{r.count} customer{r.count !== 1 ? "s" : ""} referred</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={2500} />
    </div>
  );
};

export default CRM;
